/*
  ExerciseProgramEditor — the working surface for a client's exercise
  program. Handles:
    - Structured section + exercise editing
    - Live "Run safety check" against the clinical rules engine
    - Warning acknowledgement with reason
    - Status transitions (Draft → Awaiting → Active …) with
      finalisation gate on outstanding critical warnings
    - Amendment flow (locked programs create a new versioned draft)
*/
import { todayISO as melToday } from "@/lib/intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Loader2, Plus, Trash2, ChevronUp, ChevronDown, AlertTriangle,
  ShieldCheck, Save, Sparkles, Lock, CheckCircle2, GripVertical, Eye, EyeOff,
  Copy, Wand2, Printer, ArrowUpToLine, ArrowRightLeft, Scissors, Layers, BookmarkPlus,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ExerciseProgramA4Preview from "./ExerciseProgramA4Preview";
import ExerciseProgramWorksheetPreview from "./ExerciseProgramWorksheetPreview";
import PainLogPanel from "./PainLogPanel";
import SetBreakdownEditor from "./SetBreakdownEditor";
import { SaveAsBlockDialog, InsertBlockDialog } from "./ProgramBlockDialogs";
import { printPdfFromUrl } from "@/lib/printPdf";
import { useUiPref } from "@/lib/uiPrefs";

const LOCKED = new Set(["active", "superseded", "completed", "archived"]);

const DEFAULT_SECTION_TYPES = [
  { key: "warmup", label: "Warm-up" },
  { key: "mobility", label: "Mobility" },
  { key: "balance", label: "Balance" },
  { key: "strength", label: "Strength" },
  { key: "cardio", label: "Cardio" },
  { key: "pilates_reformer", label: "Pilates · Reformer" },
  { key: "pilates_trapeze", label: "Pilates · Trapeze" },
  { key: "pilates_chair", label: "Pilates · Combo Chair" },
  { key: "pilates_mat", label: "Pilates · Mat" },
  { key: "onero", label: "Onero" },
  { key: "glad", label: "GLA:D" },
  { key: "neuro", label: "Parkinson's / Neuro" },
  { key: "home", label: "Home HEP" },
  { key: "cooldown", label: "Cool-down" },
  { key: "other", label: "Other" },
];


export default function ExerciseProgramEditor({ programId, open, onOpenChange }) {
  const { can } = useAuth();
  const canManageTemplates = can("owner", "manager");
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Autosave state — shown as a subtle pill in the header. Drafts
  // autosave 1.5s after the last edit; locked programs are never
  // autosaved (they need an explicit amendment reason).
  const [autoSaveState, setAutoSaveState] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSavedPayloadRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveInFlightRef = useRef(false);
  const skipNextAutoSaveRef = useRef(true); // skip the very first doc-load
  // Section-type dropdown catalog. Falls back to the historic 15
  // defaults if the fetch fails so we never crash the editor over a
  // dropdown lookup. See SectionCategoriesCard for the CRUD UI.
  const [sectionTypes, setSectionTypes] = useState(DEFAULT_SECTION_TYPES);
  useEffect(() => {
    let cancelled = false;
    api.get("/exercise-programs/section-categories")
      .then((r) => {
        if (cancelled) return;
        const items = (r.data?.items || []).map((i) => ({
          key: i.key, label: i.label,
        }));
        if (items.length > 0) setSectionTypes(items);
      })
      .catch(() => { /* keep the fallback */ });
    return () => { cancelled = true; };
  }, [open]);
  const [evaluation, setEvaluation] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [amendReason, setAmendReason] = useState("");
  const [amending, setAmending] = useState(false);
  const [previewOpen, setPreviewOpen] = useUiPref("exercise_program_preview_open", true);
  // Layout, colour mode and the default orientation for new programs are
  // per-practitioner account prefs. Orientation is still remembered PER
  // PROGRAM on ``worksheet_settings.orientation`` once set.
  const [previewLayout, setPreviewLayout] = useUiPref("exercise_program_layout", "worksheet", "ep-preview-layout");
  const [orientationDefault, setOrientationDefault] = useUiPref("exercise_program_pdf_orientation", "landscape", "ep-pdf-orientation");
  const [pdfOrientation, setPdfOrientationState] = useState(orientationDefault);
  // Colour mode for the PDF/preview. Default is BW to save ink at
  // print time; operators toggle to Colour when they specifically
  // want the indigo accents (e.g. a laminated clinic reference copy).
  const [pdfColorMode, setPdfColorMode] = useUiPref("exercise_program_color_mode", "bw", "ep-pdf-color-mode");
  const [worksheetEdit, setWorksheetEdit] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [printOptsOpen, setPrintOptsOpen] = useState(false);
  // One-time print-time overrides. When null, the persisted
  // per-program worksheet_settings values are used.
  const [printOverrideNotes, setPrintOverrideNotes] = useState(null);
  const [printOverrideFit, setPrintOverrideFit] = useState(null);
  // Reusable program block flows — MVP snapshot semantics.
  const [saveBlockOpen, setSaveBlockOpen] = useState(false);
  const [saveBlockCtx, setSaveBlockCtx] = useState(null); // { index, section }
  const [insertBlockOpen, setInsertBlockOpen] = useState(false);

  // Stamp every section coming from the API with a local `_dnd_id`
  // so @dnd-kit has a stable identifier across renders. We keep the
  // id in local state only — `stripDndIds()` scrubs it out of every
  // payload we PUT back to the backend so it never persists to Mongo.
  const stampSections = useCallback((d) => {
    if (!d) return d;
    let seq = 0;
    const secs = (d.sections || []).map((s) => (
      s && s._dnd_id ? s : { ...s, _dnd_id: `sec-${Date.now()}-${seq++}` }
    ));
    return { ...d, sections: secs };
  }, []);

  /**
   * Strip the local `_dnd_id` (and other underscore-prefixed private
   * fields) off sections + exercises before we PUT the payload to the
   * backend. Runs deep enough to cover set-groups and exercise
   * sub-objects but stays shallow otherwise — no clone-happy JSON
   * round-trip so we don't accidentally serialise Dates.
   */
  const stripDndIds = useCallback((sections) => {
    return (sections || []).map((s) => {
      // eslint-disable-next-line no-unused-vars
      const { _dnd_id, ...rest } = s || {};
      return rest;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/exercise-programs/${programId}`);
      setDoc(stampSections(r.data));
    } catch (e) {
      toast.error("Couldn't load program", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [programId, stampSections]);

  useEffect(() => { if (open) { load(); setEvaluation(null); } }, [open, load]);

  const locked = doc && LOCKED.has(doc.status);
  // Inputs stay editable regardless of `locked` status — the amendment
  // reason is enforced at save time by submitAmendment(). `locked` still
  // controls which save button shows (Save Draft vs Create Amendment).
  const inputsDisabled = false;

  function updateLocal(patch) {
    setDoc((d) => ({ ...d, ...patch }));
  }

  // Hydrate the orientation from the program document — honour its
  // saved orientation, else the practitioner's account default.
  useEffect(() => {
    if (!doc) return;
    const saved = doc.worksheet_settings?.orientation;
    setPdfOrientationState(saved === "portrait" || saved === "landscape" ? saved : orientationDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, orientationDefault]);

  // Changing orientation (a) updates local state, (b) saves it as the
  // practitioner's account default for new programs, and (c) PATCHes
  // worksheet_settings so it sticks the next time this program opens.
  const setPdfOrientation = useCallback((next) => {
    setPdfOrientationState(next);
    setOrientationDefault(next);
    if (!doc?.id) return;
    const settings = { ...(doc.worksheet_settings || {}), orientation: next };
    updateLocal({ worksheet_settings: settings });
    api.patch(`/exercise-programs/${doc.id}`, { worksheet_settings: settings })
      .then((r) => setDoc(stampSections(r.data)))
      .catch((err) => {
        toast.error("Couldn't remember orientation for this program", {
          description: err?.response?.data?.detail || err.message,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.worksheet_settings, stampSections]);

  // Active Aging programs default to SOAP-notes HIDDEN because the
  // group-class format doesn't run session-by-session SOAP notes.
  // The user can flip either default per program via the toolbar.
  const isActiveAgingProgram = useMemo(() => {
    if (!doc) return false;
    const variant = String(doc?.source?.layout_variant || "").toLowerCase();
    if (variant.startsWith("active_aging")) return true;
    const svc = String(doc?.service_type || "").toLowerCase();
    if (svc.includes("active_aging") || svc.includes("active aging") || svc.includes("active-aging")) return true;
    const tags = (doc?.tags || []).map((t) => String(t).toLowerCase());
    if (tags.some((t) => t.includes("active") && t.includes("aging"))) return true;
    const title = String(doc?.title || "").toLowerCase();
    return title.includes("active aging") || title.includes("active-aging");
  }, [doc]);

  const showSoapNotes = useMemo(() => {
    if (!doc) return true;
    const v = doc?.worksheet_settings?.show_soap_notes;
    if (typeof v === "boolean") return v;
    return !isActiveAgingProgram;
  }, [doc, isActiveAgingProgram]);

  const fitToOnePage = !!doc?.worksheet_settings?.fit_to_one_page;

  const patchWorksheetSettings = useCallback(async (patch, errorLabel) => {
    if (!doc?.id) return;
    const settings = { ...(doc.worksheet_settings || {}), ...patch };
    updateLocal({ worksheet_settings: settings });
    try {
      const r = await api.patch(`/exercise-programs/${doc.id}`, {
        worksheet_settings: settings,
      });
      setDoc(stampSections(r.data));
    } catch (err) {
      toast.error(errorLabel || "Couldn't update setting", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  }, [doc?.id, doc?.worksheet_settings, stampSections]);

  // Print/PDF URL builder — honours the per-print override (from the
  // small "options" popover next to Print) when present, otherwise
  // uses the program's persisted worksheet_settings via the server-
  // side defaults. Passing include_notes / fit_pages explicitly means
  // the backend won't second-guess the practitioner's intent.
  const buildPrintUrl = useCallback((forceIncludeOverrides = false) => {
    const params = new URLSearchParams({
      layout: previewLayout, orientation: pdfOrientation, color_mode: pdfColorMode,
    });
    const notesFinal = forceIncludeOverrides
      ? (printOverrideNotes ?? showSoapNotes)
      : (printOverrideNotes !== null ? printOverrideNotes : showSoapNotes);
    const fitFinal = forceIncludeOverrides
      ? (printOverrideFit ?? fitToOnePage)
      : (printOverrideFit !== null ? printOverrideFit : fitToOnePage);
    params.set("include_notes", notesFinal ? "true" : "false");
    params.set("fit_pages", fitFinal ? "1" : "2");
    return `/api/exercise-programs/${programId}/pdf?${params.toString()}`;
  }, [programId, previewLayout, pdfOrientation, pdfColorMode,
      printOverrideNotes, printOverrideFit, showSoapNotes, fitToOnePage]);

  const handlePrintNow = useCallback(async () => {
    const url = buildPrintUrl(true);
    // If Fit-to-one-page is requested, preflight to warn when the
    // program is too dense to fit even at the minimum font size.
    const wantsOne = (printOverrideFit !== null ? printOverrideFit : fitToOnePage);
    if (wantsOne) {
      try {
        const notesFinal = printOverrideNotes !== null ? printOverrideNotes : showSoapNotes;
        const params = new URLSearchParams({
          layout: previewLayout, orientation: pdfOrientation, color_mode: pdfColorMode,
          include_notes: notesFinal ? "true" : "false", fit_pages: "1",
        });
        const r = await api.get(`/exercise-programs/${programId}/pdf/fit-check?${params.toString()}`);
        if (r.data && r.data.fits === false) {
          toast.warning("Couldn't fit on 1 page", {
            description: `Program overflows to ${r.data.pages} pages at the minimum font size (${r.data.chosen_pt}pt). Consider trimming exercises or turning off Fit-to-one-page.`,
          });
        }
      } catch {
        // Non-fatal — proceed with the print regardless.
      }
    }
    // Clear one-off overrides after firing so subsequent prints go
    // back to the persisted per-program defaults.
    try {
      await printPdfFromUrl(url);
    } catch (err) {
      toast.error("Print failed", { description: err.message });
      return;
    }
    setPrintOverrideNotes(null);
    setPrintOverrideFit(null);
  }, [buildPrintUrl, printOverrideFit, printOverrideNotes, fitToOnePage,
      showSoapNotes, previewLayout, pdfOrientation, pdfColorMode, programId]);

  function updateSection(i, patch) {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      sections[i] = { ...sections[i], ...patch };
      return { ...d, sections };
    });
  }

  function updateExercise(sIdx, eIdx, patch) {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      const exs = [...(sections[sIdx].exercises || [])];
      exs[eIdx] = { ...exs[eIdx], ...patch };
      sections[sIdx] = { ...sections[sIdx], exercises: exs };
      return { ...d, sections };
    });
  }

  function addSection() {
    const newIndex = (doc?.sections || []).length;
    setDoc((d) => ({
      ...d,
      sections: [...(d.sections || []),
                  { _dnd_id: `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    key: "other", label: "New section", narrative: "", exercises: [] }],
    }));
    // Toolbar sits at the top — bring the new (last) section into view.
    setTimeout(() => {
      document.querySelector(`[data-testid="ep-section-${newIndex}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }

  function removeSection(i) {
    setDoc((d) => ({ ...d, sections: (d.sections || []).filter((_, k) => k !== i) }));
  }

  function moveSection(i, dir) {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      const j = i + dir;
      if (j < 0 || j >= sections.length) return d;
      [sections[i], sections[j]] = [sections[j], sections[i]];
      return { ...d, sections };
    });
  }

  // Drag-to-reorder wiring. `sensors` picks up either a 4px pointer
  // drag (so a plain click on the handle doesn't accidentally
  // trigger a move) or keyboard navigation for a11y.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleSectionDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeType = active.data?.current?.type;
    // Route exercise drags to a dedicated handler; only sections here.
    if (activeType === "exercise") {
      handleExerciseDragEnd(event);
      return;
    }
    setDoc((d) => {
      const sections = d?.sections || [];
      const from = sections.findIndex((s) => s._dnd_id === active.id);
      const to = sections.findIndex((s) => s._dnd_id === over.id);
      if (from < 0 || to < 0) return d;
      return { ...d, sections: arrayMove(sections, from, to) };
    });
  }

  // Cross-section (and within-section) exercise drag. Resolves the
  // source/destination section by `_dnd_id` (stable across renders)
  // and the destination index either from the target exercise's
  // position or by appending when the operator drops onto an empty
  // section's drop zone.
  function handleExerciseDragEnd(event) {
    const { active, over } = event;
    if (!over) return;
    const srcSecDndId = active.data?.current?.sectionDndId;
    const activeExId = active.data?.current?.exId;
    if (!srcSecDndId || !activeExId) return;

    const overType = over.data?.current?.type;
    let dstSecDndId = null;
    let dstExId = null; // target exercise id (drop-before)
    if (overType === "exercise") {
      dstSecDndId = over.data?.current?.sectionDndId;
      dstExId = over.data?.current?.exId;
    } else if (overType === "section-dropzone") {
      dstSecDndId = over.data?.current?.sectionDndId;
    } else {
      return;
    }
    if (!dstSecDndId) return;
    if (active.id === over.id) return;

    setDoc((d) => {
      const sections = [...(d?.sections || [])];
      const srcIdx = sections.findIndex((s) => s._dnd_id === srcSecDndId);
      const dstIdx = sections.findIndex((s) => s._dnd_id === dstSecDndId);
      if (srcIdx < 0 || dstIdx < 0) return d;

      if (srcIdx === dstIdx) {
        // Same-section reorder — arrayMove using target exercise id.
        const exs = [...(sections[srcIdx].exercises || [])];
        const from = exs.findIndex((e) => e.id === activeExId);
        if (from < 0) return d;
        let to;
        if (dstExId) {
          to = exs.findIndex((e) => e.id === dstExId);
          if (to < 0) return d;
        } else {
          to = exs.length - 1; // append-to-end drop zone
        }
        sections[srcIdx] = { ...sections[srcIdx], exercises: arrayMove(exs, from, to) };
        return { ...d, sections };
      }

      // Cross-section move.
      const srcExs = [...(sections[srcIdx].exercises || [])];
      const fromIdx = srcExs.findIndex((e) => e.id === activeExId);
      if (fromIdx < 0) return d;
      const [moved] = srcExs.splice(fromIdx, 1);
      if (!moved) return d;
      sections[srcIdx] = { ...sections[srcIdx], exercises: srcExs };

      const dstExs = [...(sections[dstIdx].exercises || [])];
      let insertAt = dstExs.length;
      if (dstExId) {
        const t = dstExs.findIndex((e) => e.id === dstExId);
        if (t >= 0) insertAt = t;
      }
      dstExs.splice(insertAt, 0, moved);
      sections[dstIdx] = { ...sections[dstIdx], exercises: dstExs };
      return { ...d, sections };
    });
  }

  function addExercise(sIdx) {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      const exs = [...(sections[sIdx].exercises || []),
                    { id: crypto.randomUUID(), name: "", sets: "", reps: "" }];
      sections[sIdx] = { ...sections[sIdx], exercises: exs };
      return { ...d, sections };
    });
  }

  function removeExercise(sIdx, eIdx) {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      sections[sIdx] = {
        ...sections[sIdx],
        exercises: (sections[sIdx].exercises || []).filter((_, k) => k !== eIdx),
      };
      return { ...d, sections };
    });
  }

  function moveExercise(sIdx, eIdx, dir) {
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      const exs = [...(sections[sIdx].exercises || [])];
      const j = eIdx + dir;
      if (j < 0 || j >= exs.length) return d;
      [exs[eIdx], exs[j]] = [exs[j], exs[eIdx]];
      sections[sIdx] = { ...sections[sIdx], exercises: exs };
      return { ...d, sections };
    });
  }

  function moveExerciseToSection(sIdx, eIdx, targetIdx) {
    setDoc((d) => {
      if (targetIdx === sIdx) return d;
      const sections = [...(d.sections || [])];
      if (targetIdx < 0 || targetIdx >= sections.length) return d;
      const srcExs = [...(sections[sIdx].exercises || [])];
      const [moved] = srcExs.splice(eIdx, 1);
      if (!moved) return d;
      sections[sIdx] = { ...sections[sIdx], exercises: srcExs };
      const dstExs = [...(sections[targetIdx].exercises || []), moved];
      sections[targetIdx] = { ...sections[targetIdx], exercises: dstExs };
      return { ...d, sections };
    });
  }

  function mergeSectionIntoPrevious(sIdx) {
    if (sIdx <= 0) return;
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      if (sIdx >= sections.length) return d;
      const prev = sections[sIdx - 1];
      const curr = sections[sIdx];
      sections[sIdx - 1] = {
        ...prev,
        exercises: [...(prev.exercises || []), ...(curr.exercises || [])],
      };
      sections.splice(sIdx, 1);
      return { ...d, sections };
    });
  }

  function splitSectionAfter(sIdx, exerciseIndex) {
    // Split `sections[sIdx]` in two: everything up to & including
    // exerciseIndex stays, the rest moves into a fresh section
    // inserted immediately after. Handy when a warm-up or main-block
    // section has grown too broad and needs to be sub-grouped.
    setDoc((d) => {
      const sections = [...(d.sections || [])];
      if (sIdx < 0 || sIdx >= sections.length) return d;
      const src = sections[sIdx];
      const exs = [...(src.exercises || [])];
      if (exerciseIndex < 0 || exerciseIndex >= exs.length - 1) return d;
      const keep = exs.slice(0, exerciseIndex + 1);
      const rest = exs.slice(exerciseIndex + 1);
      const baseLabel = src.label || src.key || "Section";
      // Auto-derive a suffix (2, 3, 4…) if the operator has already
      // split this section before.
      let suffix = 2;
      const siblingLabels = new Set(sections.map((s) => s.label));
      while (siblingLabels.has(`${baseLabel} (${suffix})`)) suffix += 1;
      const newKey = `${(src.key || "section")}-${suffix}-${Math.random().toString(36).slice(2, 6)}`;
      const newSection = {
        key: newKey,
        label: `${baseLabel} (${suffix})`,
        narrative: "",
        exercises: rest,
      };
      sections[sIdx] = { ...src, exercises: keep };
      sections.splice(sIdx + 1, 0, newSection);
      return { ...d, sections };
    });
  }

  // Extract the payload the backend needs to persist a draft — the
  // same shape is used by manual saveDraft() AND autosave, so pin it
  // to a helper to keep them in sync (adding a new field once
  // updates both flows).
  const buildDraftPayload = useCallback(() => {
    if (!doc) return null;
    return {
      title: doc.title,
      service_type: doc.service_type,
      tags: doc.tags,
      sections: stripDndIds(doc.sections),
      review_date: doc.review_date,
      goals: doc.goals,
      precautions: doc.precautions,
      precautions_pathologies: doc.precautions_pathologies,
      worksheet_settings: doc.worksheet_settings,
      pain_tracking_enabled: !!doc.pain_tracking_enabled,
    };
  }, [doc, stripDndIds]);

  async function saveDraft() {
    setSaving(true);
    try {
      const payload = buildDraftPayload();
      const r = await api.patch(`/exercise-programs/${programId}`, payload);
      setDoc(stampSections(r.data));
      // Sync autosave bookkeeping too so the header pill flips to
      // "Saved" and no phantom autosave fires right after.
      lastSavedPayloadRef.current = JSON.stringify(payload);
      setAutoSaveState("saved");
      setLastSavedAt(Date.now());
      toast.success("Draft saved");
    } catch (e) {
      setAutoSaveState("error");
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  // ---- Autosave ----------------------------------------------------------
  // Fires 1.5s after the last edit to a DRAFT program. Locked
  // (active/superseded/completed/archived) programs are never
  // autosaved — those need an explicit amendment reason. Silent by
  // design: no toast on success, only on failure the header pill
  // turns red.
  const AUTOSAVE_DEBOUNCE_MS = 1500;
  useEffect(() => {
    if (!doc || loading) return;
    if (LOCKED.has(doc.status)) return;
    // The very first render after a load is not a user edit — skip
    // it so we don't PATCH the same doc back at the server.
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      lastSavedPayloadRef.current = JSON.stringify(buildDraftPayload());
      return;
    }
    const payload = buildDraftPayload();
    const serialised = JSON.stringify(payload);
    if (serialised === lastSavedPayloadRef.current) return;

    // Debounce: cancel any pending timer and schedule a new one.
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      if (autoSaveInFlightRef.current) return;
      autoSaveInFlightRef.current = true;
      setAutoSaveState("saving");
      try {
        const r = await api.patch(`/exercise-programs/${programId}`, payload);
        // If the user has kept typing (payload changed) since we
        // fired, don't clobber the local doc with the server echo —
        // just update `lastSavedPayloadRef` so the next diff check
        // works, and let the next debounced tick catch up.
        const currentSerialised = JSON.stringify(buildDraftPayload());
        if (currentSerialised === serialised) {
          // Merge ONLY the server-authoritative metadata (version,
          // updated_at, updated_by, audit trail) into the local doc.
          // We deliberately DON'T replace `sections` with the server
          // echo because doing so re-stamps every section's
          // `_dnd_id` — the key @dnd-kit uses — which would
          // unmount/remount every input field on the page and
          // (a) yank focus out of whatever the user was typing in
          // and (b) scroll the viewport back to the top. See the
          // "cursor jumps" bug report.
          setDoc((prev) => (prev ? {
            ...prev,
            version: r.data.version,
            updated_at: r.data.updated_at,
            updated_by: r.data.updated_by,
            audit: r.data.audit ?? prev.audit,
          } : prev));
        }
        lastSavedPayloadRef.current = serialised;
        setAutoSaveState("saved");
        setLastSavedAt(Date.now());
      } catch (e) {
        setAutoSaveState("error");
        // No toast — a silent draft autosave failure would be
        // pop-up spam. The red pill + tooltip surfaces it and the
        // manual Save Draft button still works.
        // eslint-disable-next-line no-console
        console.warn("EP autosave failed:", e?.response?.data?.detail || e.message);
      } finally {
        autoSaveInFlightRef.current = false;
      }
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [doc, loading, buildDraftPayload, programId]);

  // Reset the "skip first autosave" guard whenever a NEW program
  // opens (so opening a second draft doesn't accidentally autosave
  // on the very first render).
  useEffect(() => {
    skipNextAutoSaveRef.current = true;
    lastSavedPayloadRef.current = null;
    setAutoSaveState("idle");
    setLastSavedAt(null);
  }, [programId, open]);

  // Re-baseline the "last saved" ref whenever the doc identity
  // (or version) changes as a result of a server response. This
  // eliminates a class of phantom autosave PATCHes that could
  // otherwise fire when the server-echoed doc normalises fields
  // (e.g. worksheet_settings) differently than the pre-save local
  // payload. Any subsequent effect run will diff against this
  // fresh baseline and correctly skip.
  useEffect(() => {
    if (!doc) return;
    lastSavedPayloadRef.current = JSON.stringify(buildDraftPayload());
    // Intentionally omit buildDraftPayload from deps — we only want
    // this to run when the doc *identity* changes (id / version /
    // updated_at from the server), not on every local edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.version, doc?.updated_at]);

  // Flush a pending autosave if the user closes the dialog while a
  // debounce is still in flight — mirrors Google Docs "close but
  // finish saving" behaviour so no edit is silently dropped.
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        // Best-effort: fire the last known payload synchronously via
        // fetch keepalive so the request survives the tab-close. We
        // can't await here (cleanup can't be async), but the network
        // request is queued.
        if (!autoSaveInFlightRef.current && doc && !LOCKED.has(doc.status)) {
          const payload = buildDraftPayload();
          const serialised = JSON.stringify(payload);
          if (serialised !== lastSavedPayloadRef.current) {
            api.patch(`/exercise-programs/${programId}`, payload).catch(() => {});
          }
        }
      }
    };
    // Intentional: we only want this to run on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runSafetyCheck() {
    setEvaluating(true);
    try {
      const r = await api.post(`/exercise-programs/${programId}/evaluate`);
      setEvaluation(r.data);
      const critical = (r.data.warnings || []).filter((w) => w.severity === "critical").length;
      if (critical === 0) toast.success("Clinical safety check passed");
      else toast.warning(`${critical} critical warning(s) — review before activation`);
    } catch (e) {
      toast.error("Safety check failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setEvaluating(false);
    }
  }

  async function acknowledgeWarning(w) {
    const reason = prompt(
      `Acknowledge rule "${w.rule_id}" for "${w.exercise_name}"?\nEnter a documented reason:`,
    );
    if (!reason?.trim()) return;
    try {
      await api.post(`/exercise-programs/${programId}/acknowledge`, {
        rule_id: w.rule_id, exercise_id: w.exercise_id, reason: reason.trim(),
      });
      toast.success("Warning acknowledged");
      await load();
      await runSafetyCheck();
    } catch (e) {
      toast.error("Couldn't acknowledge", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function transitionStatus(target) {
    try {
      const body = { status: target };
      if (target === "active") {
        // If unacknowledged criticals, prompt for an override reason.
        const critical = (evaluation?.warnings || []).filter((w) => w.severity === "critical");
        const ackedIds = new Set((doc?.acknowledgements || []).map((a) => `${a.rule_id}:${a.exercise_id}`));
        const unacked = critical.filter((w) => !ackedIds.has(`${w.rule_id}:${w.exercise_id}`));
        if (unacked.length > 0) {
          const reason = prompt(
            `${unacked.length} critical warning(s) unacknowledged. Enter an override reason to activate anyway:`,
          );
          if (!reason?.trim()) return;
          body.override_warnings = true;
          body.reason = reason.trim();
        }

        // Persist any unsaved edits before flipping to active. Prior
        // to this, an operator who edited a template-instantiated
        // program (e.g. Onero) and clicked Activate without hitting
        // Save Draft first would lose every edit — the status route
        // doesn't take a doc body, so the /status POST returned the
        // last-saved (i.e. default template) doc and the local edits
        // silently disappeared. Reported 2026-02-28.
        try {
          setSaving(true);
          await api.patch(`/exercise-programs/${programId}`, {
            title: doc.title,
            service_type: doc.service_type,
            tags: doc.tags,
            sections: stripDndIds(doc.sections),
            review_date: doc.review_date,
            goals: doc.goals,
            precautions: doc.precautions,
            precautions_pathologies: doc.precautions_pathologies,
            worksheet_settings: doc.worksheet_settings,
            pain_tracking_enabled: !!doc.pain_tracking_enabled,
          });
        } finally {
          setSaving(false);
        }
      }
      const r = await api.post(`/exercise-programs/${programId}/status`, body);
      setDoc(stampSections(r.data));
      toast.success(`Program is now ${target.replace(/_/g, " ")}`);
    } catch (e) {
      toast.error("Status change failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function submitAmendment() {
    if (!amendReason.trim()) {
      toast.error("An amendment reason is required");
      return;
    }
    setAmending(true);
    try {
      const r = await api.post(`/exercise-programs/${programId}/amend`, {
        reason: amendReason.trim(),
        changes: {
          title: doc.title, sections: stripDndIds(doc.sections), tags: doc.tags,
          review_date: doc.review_date, goals: doc.goals, precautions: doc.precautions,
          precautions_pathologies: doc.precautions_pathologies,
          worksheet_settings: doc.worksheet_settings,
        },
      });
      setDoc(stampSections(r.data));
      setAmendReason("");
      toast.success(`New amendment v${r.data.version} created (in draft)`);
    } catch (e) {
      toast.error("Amendment failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setAmending(false);
    }
  }

  const acked = useMemo(() => {
    const s = new Set();
    for (const a of doc?.acknowledgements || []) s.add(`${a.rule_id}:${a.exercise_id}`);
    return s;
  }, [doc]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96vw] w-[96vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="exercise-program-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {doc?.title || "Exercise program"}
            {doc && (
              <Badge className={cn("text-2xs uppercase border",
                                     LOCKED.has(doc.status)
                                       ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                                       : "bg-blue-50 text-blue-800 border-blue-300")}>
                {(doc.status || "draft").replace(/_/g, " ")}
              </Badge>
            )}
            {doc && (
              <span className="text-xs text-zinc-500 font-normal">v{doc.version}</span>
            )}
            {locked && <Lock className="h-3.5 w-3.5 text-amber-500" title="Active/finalised version — edits will create a new amendment on save" />}
            {doc && !locked && (
              <AutoSaveIndicator state={autoSaveState} lastSavedAt={lastSavedAt} />
            )}
            <div className="flex-1" />
            {canManageTemplates && doc && (
              <Button
                size="sm" variant="outline"
                onClick={() => setSaveAsOpen(true)}
                className="h-7 text-xs"
                data-testid="ep-save-as-template"
                title="Save this program as a clinic template"
              >
                <Wand2 className="h-3.5 w-3.5 mr-1" /> Save as template
              </Button>
            )}
            <Button
              size="sm"
              variant={previewOpen ? "default" : "outline"}
              onClick={() => setPreviewOpen((v) => !v)}
              className="h-7 text-xs"
              data-testid="ep-preview-toggle"
            >
              {previewOpen
                ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide preview</>
                : <><Eye className="h-3.5 w-3.5 mr-1" /> Show preview</>}
            </Button>
            {previewOpen && (
              <select
                value={previewLayout}
                onChange={(e) => setPreviewLayout(e.target.value)}
                className="h-7 text-xs border border-zinc-200 rounded px-1 bg-white"
                data-testid="ep-preview-layout"
                title="Preview layout"
              >
                <option value="worksheet">Worksheet</option>
                <option value="compact">Compact</option>
              </select>
            )}
            {doc && (
              <select
                value={pdfOrientation}
                onChange={(e) => setPdfOrientation(e.target.value)}
                className="h-7 text-xs border border-zinc-200 rounded px-1 bg-white"
                data-testid="ep-pdf-orientation"
                title="PDF orientation for print"
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            )}
            {doc && (
              <select
                value={pdfColorMode}
                onChange={(e) => setPdfColorMode(e.target.value)}
                className="h-7 text-xs border border-zinc-200 rounded px-1 bg-white"
                data-testid="ep-pdf-color-mode"
                title="Ink mode — BW saves toner (default); Colour keeps the indigo accents"
              >
                <option value="bw">B&amp;W (save ink)</option>
                <option value="color">Colour</option>
              </select>
            )}
            {doc && previewLayout === "worksheet" && (
              <label
                className="h-7 inline-flex items-center gap-1.5 px-2 rounded border border-zinc-200 text-xs bg-white cursor-pointer hover:bg-zinc-50"
                title="Add a Rate of Perceived Exertion (0-10) column to the worksheet — used mainly on active-ageing programs"
                data-testid="ep-rpe-toggle"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-indigo-600"
                  checked={!!(doc?.worksheet_settings?.show_rpe_column)}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    const settings = { ...(doc.worksheet_settings || {}), show_rpe_column: next };
                    updateLocal({ worksheet_settings: settings });
                    try {
                      const r = await api.patch(`/exercise-programs/${programId}`, {
                        worksheet_settings: settings,
                      });
                      setDoc(stampSections(r.data));
                    } catch (err) {
                      toast.error("Couldn't update RPE column", {
                        description: err?.response?.data?.detail || err.message,
                      });
                      updateLocal({
                        worksheet_settings: { ...settings, show_rpe_column: !next },
                      });
                    }
                  }}
                />
                RPE column
              </label>
            )}
            {doc && previewLayout === "worksheet" && (
              <label
                className="h-7 inline-flex items-center gap-1.5 px-2 rounded border border-zinc-200 text-xs bg-white cursor-pointer hover:bg-zinc-50"
                title={
                  isActiveAgingProgram
                    ? "Active Aging programs hide the SOAP Notes section by default — toggle on to show it."
                    : "Hide the Session Notes (SOAP) block in the editor and on the printed worksheet."
                }
                data-testid="ep-soap-notes-toggle"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-indigo-600"
                  checked={showSoapNotes}
                  onChange={(e) => patchWorksheetSettings(
                    { show_soap_notes: e.target.checked },
                    "Couldn't update SOAP Notes visibility",
                  )}
                />
                SOAP notes
              </label>
            )}
            {doc && (
              <label
                className="h-7 inline-flex items-center gap-1.5 px-2 rounded border border-zinc-200 text-xs bg-white cursor-pointer hover:bg-zinc-50"
                title="Try to squeeze the whole printed program onto a single page (shrinks font down to 7pt if needed; overflows to page 2 with a warning if still too big)."
                data-testid="ep-fit-one-page-toggle"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-indigo-600"
                  checked={fitToOnePage}
                  onChange={(e) => patchWorksheetSettings(
                    { fit_to_one_page: e.target.checked },
                    "Couldn't update Fit-to-one-page setting",
                  )}
                />
                Fit to 1 page
              </label>
            )}
            {doc && (
              <a
                href={buildPrintUrl(false)}
                target="_blank" rel="noreferrer"
                className="h-7 inline-flex items-center px-2 rounded border border-zinc-200 text-xs hover:bg-zinc-100"
                data-testid="ep-print-pdf"
                title={`Open PDF (${previewLayout} · ${pdfOrientation} · ${pdfColorMode})`}
              >
                <Save className="h-3.5 w-3.5 mr-1" /> PDF
              </a>
            )}
            {doc && (
              <Popover open={printOptsOpen} onOpenChange={setPrintOptsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="h-7 inline-flex items-center px-1.5 rounded border border-zinc-200 text-xs hover:bg-zinc-100"
                    data-testid="ep-print-options-trigger"
                    title="Override print options for this one print"
                  >
                    <Settings className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 p-3 space-y-3">
                  <div className="text-xs font-semibold text-zinc-800">Print options (one-time)</div>
                  <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-indigo-600"
                      checked={printOverrideNotes ?? showSoapNotes}
                      onChange={(e) => setPrintOverrideNotes(e.target.checked)}
                      data-testid="ep-print-override-notes"
                    />
                    Include SOAP notes
                  </label>
                  <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-indigo-600"
                      checked={printOverrideFit ?? fitToOnePage}
                      onChange={(e) => setPrintOverrideFit(e.target.checked)}
                      data-testid="ep-print-override-fit"
                    />
                    Fit to 1 page
                  </label>
                  <div className="text-2xs text-zinc-500 leading-snug">
                    Defaults come from this program's saved settings. Changes here apply to the next print/PDF only.
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      onClick={() => { setPrintOverrideNotes(null); setPrintOverrideFit(null); }}
                      data-testid="ep-print-override-reset"
                    >
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs flex-1"
                      onClick={async () => {
                        setPrintOptsOpen(false);
                        await handlePrintNow();
                      }}
                      data-testid="ep-print-override-apply"
                    >
                      <Printer className="h-3.5 w-3.5 mr-1" /> Print now
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {doc && (
              <button
                type="button"
                onClick={() => handlePrintNow()}
                className="h-7 inline-flex items-center px-2 rounded border border-zinc-200 text-xs hover:bg-zinc-100"
                data-testid="ep-print-now"
                title="Send this program straight to the printer"
              >
                <Printer className="h-3.5 w-3.5 mr-1" /> Print
              </button>
            )}
          </DialogTitle>
          <DialogDescription>
            {(doc?.client_name || doc?.client_email) && (
              <span
                className="text-zinc-700 font-medium mr-2"
                data-testid="editor-client-name"
              >
                Client: {(doc.client_name || "").trim() || doc.client_email}
              </span>
            )}
            {(doc?.quality_issues || []).length > 0 && (
              <span className="text-amber-800">
                Parser flagged {doc.quality_issues.length} item(s) for review · {" "}
                {doc.quality_issues.join(" · ")}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading || !doc ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : (
          <div className={cn(
            "flex-1 grid grid-cols-1 gap-3 overflow-hidden",
            previewOpen
              ? "md:grid-cols-[2fr_1.5fr_1fr]"
              : "md:grid-cols-3",
          )}>
            <div className={cn(
              "overflow-y-auto pr-1 space-y-3",
              previewOpen ? "" : "md:col-span-2",
            )}>
              <div
                className="sticky top-0 z-20 flex items-center gap-2 py-2 bg-white/95 backdrop-blur-sm border-b border-zinc-100"
                data-testid="ep-sections-toolbar"
              >
                <Button size="sm" variant="outline" onClick={addSection} disabled={inputsDisabled} data-testid="ep-add-section">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add section
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setInsertBlockOpen(true)}
                  disabled={inputsDisabled}
                  data-testid="ep-insert-block"
                  title="Insert a saved reusable block from the library"
                >
                  <Layers className="h-3.5 w-3.5 mr-1" /> Insert from library
                </Button>
                <span className="ml-auto text-2xs text-zinc-500" data-testid="ep-section-count">
                  {(doc.sections || []).length} section{(doc.sections || []).length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input
                  value={doc.title || ""}
                  onChange={(e) => updateLocal({ title: e.target.value })}
                  disabled={inputsDisabled}
                  placeholder="Program title"
                  data-testid="ep-title"
                />
                <Input
                  value={(doc.tags || []).join(", ")}
                  onChange={(e) => updateLocal({ tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  disabled={inputsDisabled}
                  placeholder="Tags — comma separated"
                  data-testid="ep-tags"
                />
              </div>

              <div>
                <label className="text-2xs uppercase tracking-wide text-zinc-500">
                  Precautions / pathologies (worksheet header)
                </label>
                <Textarea
                  // Show the same value the worksheet preview / PDF export
                  // display: `precautions_pathologies` (the modern string
                  // field) OR — for legacy programs where import hydrated
                  // the old `precautions` array from client metadata — the
                  // joined array. Without this fallback the operator sees
                  // precautions rendered on the preview but a blank input,
                  // and has no way to correct them.
                  value={
                    doc.precautions_pathologies !== undefined && doc.precautions_pathologies !== null
                      ? doc.precautions_pathologies
                      : ((doc.precautions || []).join("; "))
                  }
                  onChange={(e) => updateLocal({
                    precautions_pathologies: e.target.value,
                    // Drop the legacy array on first edit so
                    // `precautions_pathologies` becomes the single source
                    // of truth. Preview / PDF already prefer the string,
                    // so this only removes the stale array ghost.
                    precautions: [],
                  })}
                  disabled={inputsDisabled}
                  rows={2}
                  placeholder="e.g. Osteoporosis (T-score -3.1 L1) · Avoid loaded lumbar flexion · Uncontrolled HTN screened out"
                  className="text-sm"
                  data-testid="ep-precautions-pathologies"
                />
              </div>

              <PainLogPanel
                programId={programId}
                doc={doc}
                locked={locked}
                onToggle={(v) => updateLocal({ pain_tracking_enabled: v })}
                onDocRefresh={setDoc}
              />

              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <SortableContext
                  items={(doc.sections || []).map((s) => s._dnd_id)}
                  strategy={verticalListSortingStrategy}
                >
              {(doc.sections || []).map((s, i) => {
                // If the stored section key isn't in the current
                // catalog (category was deleted or renamed to a
                // different key after this section was saved), tack
                // it on so the operator still sees the exact value
                // they were saved with. Prevents a silent flip to
                // the first option on next save.
                const inCatalog = sectionTypes.some((t) => t.key === s.key);
                const options = inCatalog
                  ? sectionTypes
                  : [...sectionTypes,
                      { key: s.key, label: s.label || s.key || "(removed)" }];
                return (
                <SortableSection key={s._dnd_id} id={s._dnd_id} testId={`ep-section-${i}`}>
                  {(sortable) => (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      {...sortable.attributes}
                      {...sortable.listeners}
                      className="cursor-grab active:cursor-grabbing p-1 text-zinc-400 hover:text-zinc-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                      title="Drag to reorder section"
                      aria-label={`Drag ${s.label || s.key || "section"} section`}
                      data-testid={`ep-section-drag-${i}`}
                      disabled={inputsDisabled}
                    >
                      <GripVertical className="h-3.5 w-3.5" />
                    </button>
                    <select
                      value={s.key}
                      onChange={(e) => updateSection(i, { key: e.target.value })}
                      className="text-xs border border-zinc-200 rounded px-1.5 py-1 bg-white"
                      disabled={inputsDisabled}
                      data-testid={`ep-section-type-${i}`}
                    >
                      {options.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                    <Input
                      value={s.label || ""}
                      onChange={(e) => updateSection(i, { label: e.target.value })}
                      className="flex-1 h-8 text-sm"
                      disabled={inputsDisabled}
                    />
                    <Button size="sm" variant="ghost" onClick={() => moveSection(i, -1)} disabled={inputsDisabled || i === 0}><ChevronUp className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => moveSection(i, +1)} disabled={inputsDisabled}><ChevronDown className="h-3.5 w-3.5" /></Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => {
                        if (window.confirm(`Merge "${s.label || s.key || "section"}" into "${(doc.sections[i - 1] || {}).label || "the previous section"}"? All its exercises will move up.`)) {
                          mergeSectionIntoPrevious(i);
                        }
                      }}
                      disabled={inputsDisabled || i === 0}
                      title="Merge into previous section"
                      data-testid={`ep-merge-section-${i}`}
                    >
                      <ArrowUpToLine className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => {
                        setSaveBlockCtx({ index: i, section: s });
                        setSaveBlockOpen(true);
                      }}
                      disabled={inputsDisabled || !(s.exercises || []).length}
                      title="Save this section as a reusable Program Block"
                      data-testid={`ep-save-section-as-block-${i}`}
                    >
                      <BookmarkPlus className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeSection(i)} disabled={inputsDisabled}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </div>
                  <Textarea
                    value={s.narrative || ""}
                    onChange={(e) => updateSection(i, { narrative: e.target.value })}
                    rows={2}
                    disabled={inputsDisabled}
                    placeholder="Section narrative / practitioner cues (visible to client)"
                    className="text-sm mb-2"
                  />
                  <div className="space-y-2">
                    <SortableContext
                      items={(s.exercises || []).map((ex) => `ex:${ex.id}`)}
                      strategy={verticalListSortingStrategy}
                    >
                    {(s.exercises || []).map((ex, ei) => (
                      <SortableExerciseRow
                        key={ex.id || ei}
                        exId={ex.id}
                        sectionDndId={s._dnd_id}
                      >
                        {(sortableEx) => (
                      <ExerciseRow
                        ex={ex}
                        locked={inputsDisabled}
                        warnings={(evaluation?.warnings || []).filter(
                          (w) => w.exercise_id === ex.id,
                        )}
                        acked={acked}
                        onChange={(patch) => updateExercise(i, ei, patch)}
                        onRemove={() => removeExercise(i, ei)}
                        onMove={(dir) => moveExercise(i, ei, dir)}
                        onMoveToSection={(targetIdx) => moveExerciseToSection(i, ei, targetIdx)}
                        onSplitAfter={() => splitSectionAfter(i, ei)}
                        canSplitAfter={ei < (s.exercises || []).length - 1}
                        allSections={doc.sections || []}
                        currentSectionIndex={i}
                        onAcknowledge={acknowledgeWarning}
                        dragHandle={sortableEx}
                      />
                        )}
                      </SortableExerciseRow>
                    ))}
                    <SectionDropZone
                      sectionDndId={s._dnd_id}
                      empty={(s.exercises || []).length === 0}
                      testId={`ep-section-dropzone-${i}`}
                    />
                    </SortableContext>
                    <Button size="sm" variant="outline" onClick={() => addExercise(i)} disabled={inputsDisabled} data-testid={`ep-add-ex-${i}`}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add exercise
                    </Button>
                  </div>
                </>
                  )}
                </SortableSection>
                );
              })}
                </SortableContext>
              </DndContext>
              <SessionNotesPanel
                programId={programId}
                notes={doc.session_notes || []}
                onChanged={load}
                locked={inputsDisabled}
                hidden={!showSoapNotes}
              />
            </div>

            {/* Live A4 preview column (toggleable) */}
            {previewOpen && (
              <div className="border border-zinc-200 rounded-md overflow-hidden bg-zinc-100 flex flex-col">
                {previewLayout === "worksheet" && (
                  <div className="flex items-center gap-2 px-2 py-1 border-b border-zinc-200 bg-white">
                    <span className="text-2xs uppercase text-zinc-500 tracking-wide">
                      Worksheet
                    </span>
                    <div className="flex-1" />
                    <label className="flex items-center gap-1 text-2xs text-zinc-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={worksheetEdit}
                        onChange={(e) => setWorksheetEdit(e.target.checked)}
                        className="accent-indigo-600"
                        data-testid="ep-worksheet-edit-toggle"
                      />
                      Tap-to-log mode
                    </label>
                  </div>
                )}
                <div className="flex-1 overflow-hidden">
                  {previewLayout === "worksheet"
                    ? (
                      <ExerciseProgramWorksheetPreview
                        program={doc}
                        programId={programId}
                        editable={worksheetEdit}
                        onDailyLogSaved={load}
                        pageOrientation={pdfOrientation}
                        colorMode={pdfColorMode}
                      />
                    )
                    : <ExerciseProgramA4Preview program={doc} programId={programId} />}
                </div>
              </div>
            )}

            {/* Right rail — safety + status + amend */}
            <div className="overflow-y-auto pr-1 space-y-3">
              <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3">
                <div className="text-xs font-medium text-indigo-900 flex items-center gap-1 mb-2">
                  <ShieldCheck className="h-3.5 w-3.5" /> Clinical safety
                </div>
                <Button size="sm" onClick={runSafetyCheck} disabled={evaluating} className="w-full" data-testid="ep-run-safety">
                  {evaluating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Checking…</>
                                : <><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Run safety check</>}
                </Button>
                {evaluation && (
                  <div className="mt-2 text-xs text-indigo-900">
                    {Object.entries(evaluation.flags || {}).length === 0 ? (
                      <div>No active clinical flags on file.</div>
                    ) : (
                      <div>
                        Flags:{" "}
                        {Object.entries(evaluation.flags).map(([k, n]) => (
                          <span key={k} className="mr-1 font-mono">{k}({n})</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 space-y-2">
                      {(evaluation.warnings || []).map((w, wi) => {
                        const isAcked = acked.has(`${w.rule_id}:${w.exercise_id}`);
                        return (
                          <div
                            key={wi}
                            className={cn("rounded border p-2",
                                          w.severity === "critical" && !isAcked
                                            ? "border-red-300 bg-red-50 text-red-900"
                                            : "border-amber-300 bg-amber-50 text-amber-900")}
                            data-testid={`ep-warning-${wi}`}
                          >
                            <div className="text-2xs font-semibold uppercase flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {w.severity} · {w.exercise_name || w.exercise_id}
                              {isAcked && <span className="text-emerald-800 ml-1">· acknowledged</span>}
                            </div>
                            <div className="text-2xs mt-1">{w.message}</div>
                            {!isAcked && !inputsDisabled && (
                              <Button size="sm" variant="outline" className="mt-1 h-7 text-2xs"
                                       onClick={() => acknowledgeWarning(w)}>
                                Acknowledge with reason
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="border border-zinc-200 rounded-md p-3">
                <div className="text-xs font-medium mb-2">Actions</div>
                {!locked && (
                  <div className="space-y-2">
                    <Button size="sm" variant="outline" onClick={saveDraft} disabled={saving} className="w-full" data-testid="ep-save-draft">
                      {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
                                : <><Save className="h-3.5 w-3.5 mr-1" /> Save draft</>}
                    </Button>
                    <Button size="sm" onClick={() => transitionStatus("active")} className="w-full" data-testid="ep-activate">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Activate program
                    </Button>
                  </div>
                )}
                {locked && (
                  <div className="space-y-2">
                    <Textarea
                      value={amendReason}
                      onChange={(e) => setAmendReason(e.target.value)}
                      rows={2}
                      placeholder="Reason for amendment — required (progression, correction, etc.)"
                      className="text-sm"
                      data-testid="ep-amend-reason"
                    />
                    <Button size="sm" onClick={submitAmendment} disabled={amending || !amendReason.trim()} className="w-full" data-testid="ep-amend-submit">
                      {amending ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Amending…</>
                                  : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Create amendment</>}
                    </Button>
                  </div>
                )}
                {doc.source?.type === "docx" && (
                  <a
                    href={`/api/exercise-programs/${programId}/source`}
                    target="_blank" rel="noreferrer"
                    className="block text-xs text-indigo-700 hover:underline mt-2"
                  >
                    View original DOCX
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
      <SaveAsTemplateDialog
        open={saveAsOpen}
        onOpenChange={setSaveAsOpen}
        program={doc}
      />
      <SaveAsBlockDialog
        open={saveBlockOpen}
        onOpenChange={(v) => { setSaveBlockOpen(v); if (!v) setSaveBlockCtx(null); }}
        sourceProgramId={programId}
        sectionIndex={saveBlockCtx?.index}
        section={saveBlockCtx?.section}
      />
      <InsertBlockDialog
        open={insertBlockOpen}
        onOpenChange={setInsertBlockOpen}
        programId={programId}
        sections={doc?.sections || []}
        onInserted={load}
      />
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// SessionNotesPanel — SOAP-style session notes for the worksheet.
// Pre-fills saved notes; new notes append via POST /session-notes.
// ---------------------------------------------------------------------------
function SessionNotesPanel({ programId, notes, onChanged, locked, hidden }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => melToday());
  const [subjective, setSubjective] = useState("");
  const [objective, setObjective] = useState("");
  const [assessment, setAssessment] = useState("");
  const [plan, setPlan] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    const hasContent = [subjective, objective, assessment, plan].some((s) => s.trim());
    if (!hasContent) {
      toast.error("At least one SOAP field is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/exercise-programs/${programId}/session-notes`, {
        date,
        subjective: subjective.trim() || null,
        objective: objective.trim() || null,
        assessment: assessment.trim() || null,
        plan: plan.trim() || null,
      });
      toast.success("Session note added");
      setSubjective(""); setObjective(""); setAssessment(""); setPlan("");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this session note?")) return;
    try {
      await api.delete(`/exercise-programs/${programId}/session-notes/${id}`);
      toast.success("Note deleted");
      onChanged?.();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div
      className="border border-zinc-200 rounded-md p-3"
      data-testid="ep-session-notes-panel"
      style={hidden ? { display: "none" } : undefined}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-medium text-zinc-800">Session Notes (SOAP)</div>
        <span className="text-2xs text-zinc-500">{notes.length} recorded</span>
        <div className="flex-1" />
        <Button
          size="sm" variant="outline"
          onClick={() => setOpen((v) => !v)}
          data-testid="ep-add-session-note"
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> {open ? "Cancel" : "Add note"}
        </Button>
      </div>

      {open && (
        <div className="border border-indigo-200 bg-indigo-50/40 rounded p-2 mb-2 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-2xs uppercase text-zinc-600">Date</label>
            <Input
              type="date" value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-7 text-xs w-auto"
              data-testid="ep-session-note-date"
            />
          </div>
          {[
            ["Subjective", subjective, setSubjective, "ep-soap-s"],
            ["Objective", objective, setObjective, "ep-soap-o"],
            ["Assessment", assessment, setAssessment, "ep-soap-a"],
            ["Plan", plan, setPlan, "ep-soap-p"],
          ].map(([lbl, val, setter, testid]) => (
            <div key={lbl}>
              <label className="text-2xs uppercase text-zinc-600">{lbl}</label>
              <Textarea
                value={val}
                onChange={(e) => setter(e.target.value)}
                rows={2}
                className="text-sm"
                data-testid={testid}
              />
            </div>
          ))}
          <Button
            size="sm" onClick={save} disabled={saving}
            className="w-full" data-testid="ep-session-note-save"
          >
            {saving
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
              : <><Save className="h-3.5 w-3.5 mr-1" /> Save session note</>}
          </Button>
        </div>
      )}

      {notes.length === 0 && !open && (
        <div className="text-xs text-zinc-400 italic">
          No session notes recorded yet.
        </div>
      )}

      <div className="space-y-1.5">
        {notes.map((n) => (
          <div
            key={n.id}
            className="border-l-2 border-indigo-500 bg-white rounded-sm px-2 py-1.5"
            data-testid={`ep-session-note-${n.id}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="text-2xs uppercase font-semibold text-indigo-800 tracking-wide">
                {(n.date || "").slice(0, 10)}
              </div>
              <span className="text-2xs text-zinc-500">
                {n.created_by?.name || ""}
              </span>
              <div className="flex-1" />
              {!locked && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => remove(n.id)}
                  className="h-6 w-6 p-0"
                  title="Delete note"
                  data-testid={`ep-session-note-del-${n.id}`}
                >
                  <Trash2 className="h-3 w-3 text-red-600" />
                </Button>
              )}
            </div>
            {[["S", n.subjective], ["O", n.objective], ["A", n.assessment], ["P", n.plan]]
              .filter(([, v]) => v)
              .map(([lbl, v]) => (
                <div key={lbl} className="text-xs my-0.5">
                  <b className="text-indigo-700">{lbl}:</b>{" "}
                  <span className="text-zinc-800 whitespace-pre-wrap">{v}</span>
                </div>
              ))}
            {n.note && !n.subjective && !n.objective && !n.assessment && !n.plan && (
              <div className="text-xs text-zinc-800 whitespace-pre-wrap">{n.note}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function ExerciseRow({ ex, locked, warnings, acked, onChange, onRemove, onMove,
                       onMoveToSection, onSplitAfter, canSplitAfter,
                       allSections = [], currentSectionIndex = 0,
                       onAcknowledge, dragHandle }) {
  const criticalWarns = warnings.filter((w) => w.severity === "critical" && !acked.has(`${w.rule_id}:${ex.id}`));
  const otherSections = (allSections || [])
    .map((s, idx) => ({ idx, label: s.label || s.key || `Section ${idx + 1}` }))
    .filter((s) => s.idx !== currentSectionIndex);
  return (
    <div className={cn("border rounded p-2 bg-white",
                        criticalWarns.length > 0 ? "border-red-300 ring-1 ring-red-200 bg-red-50/30" : "border-zinc-200")}
         data-testid={`ep-exercise-${ex.id || ""}`}>
      <div className="flex items-center gap-1">
        {dragHandle ? (
          <button
            type="button"
            {...(dragHandle.attributes || {})}
            {...(dragHandle.listeners || {})}
            className="cursor-grab active:cursor-grabbing p-0.5 text-zinc-400 hover:text-zinc-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            title="Drag to move exercise (within or between sections)"
            aria-label={`Drag exercise ${ex.name || "row"}`}
            data-testid={`ep-exercise-drag-${ex.id || ""}`}
            disabled={locked}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        ) : (
          <GripVertical className="h-3.5 w-3.5 text-zinc-300" />
        )}
        <Input value={ex.name || ""} onChange={(e) => onChange({ name: e.target.value })} disabled={locked} className="flex-1 h-8 text-sm" placeholder="Exercise name" />
        {(ex.alternatives || []).length > 0 && !locked && (
          <AlternativesSwap
            alternatives={ex.alternatives}
            currentName={ex.name}
            onSwap={(alt) => onChange({
              name: alt.name,
              sets: alt.sets ?? ex.sets,
              reps: alt.reps ?? ex.reps,
              hold: alt.hold ?? ex.hold,
              load: alt.load ?? ex.load,
              band: alt.band ?? ex.band,
              tempo: alt.tempo ?? ex.tempo,
              rest: alt.rest ?? ex.rest,
              rpe: alt.rpe ?? ex.rpe,
              frequency: alt.frequency ?? ex.frequency,
              apparatus: alt.apparatus ?? ex.apparatus,
              spring: alt.spring ?? ex.spring,
              instructions: alt.instructions ?? ex.instructions,
              precautions: alt.precautions ?? ex.precautions,
            })}
          />
        )}
        <Button size="sm" variant="ghost" onClick={() => onMove(-1)} disabled={locked}><ChevronUp className="h-3 w-3" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onMove(+1)} disabled={locked}><ChevronDown className="h-3 w-3" /></Button>
        {otherSections.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              const idx = parseInt(e.target.value, 10);
              if (!Number.isNaN(idx)) onMoveToSection?.(idx);
              e.target.value = "";
            }}
            disabled={locked}
            className="h-7 text-2xs border border-zinc-200 rounded bg-white text-zinc-700 px-1"
            title="Move to another section"
            data-testid={`ep-ex-move-to-${ex.id || ""}`}
          >
            <option value="">Move to…</option>
            {otherSections.map((o) => (
              <option key={o.idx} value={o.idx}>{o.label}</option>
            ))}
          </select>
        )}
        {canSplitAfter && (
          <Button
            size="sm" variant="ghost"
            onClick={onSplitAfter}
            disabled={locked}
            title="Split section after this exercise (creates a new section for everything below)"
            data-testid={`ep-ex-split-after-${ex.id || ""}`}
          >
            <Scissors className="h-3 w-3" />
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onRemove} disabled={locked}><Trash2 className="h-3 w-3 text-red-600" /></Button>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-6 gap-1 mt-1.5">
        {[["sets", "Sets"], ["reps", "Reps"], ["hold", "Hold"], ["load", "Load"], ["tempo", "Tempo"], ["rest", "Rest"], ["rpe", "RPE"], ["frequency", "Freq"], ["side", "Side"], ["apparatus", "App"], ["spring", "Spring"], ["band", "Band"]].map(([k, lbl]) => (
          <Input
            key={k}
            value={ex[k] || ""}
            onChange={(e) => onChange({ [k]: e.target.value })}
            disabled={locked}
            placeholder={lbl}
            className="h-7 text-xs"
          />
        ))}
      </div>
      <Textarea
        value={ex.instructions || ""}
        onChange={(e) => onChange({ instructions: e.target.value })}
        disabled={locked}
        placeholder="Instructions / cues (visible to client)"
        rows={1}
        className="text-xs mt-1.5"
      />
      <Textarea
        value={ex.precautions || ""}
        onChange={(e) => onChange({ precautions: e.target.value })}
        disabled={locked}
        placeholder="Precautions"
        rows={1}
        className="text-xs mt-1.5"
      />
      <SetBreakdownEditor ex={ex} locked={locked} onChange={onChange} />
      {warnings.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {warnings.map((w, wi) => {
            const isAcked = acked.has(`${w.rule_id}:${w.exercise_id}`);
            return (
              <Badge
                key={wi}
                className={cn("text-2xs uppercase border",
                              w.severity === "critical" && !isAcked
                                ? "bg-red-50 text-red-800 border-red-300"
                                : "bg-amber-50 text-amber-800 border-amber-300")}
                title={w.message}
              >
                <AlertTriangle className="h-3 w-3 mr-1" /> {w.rule_id}
                {isAcked && " · acked"}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// AlternativesSwap — small "Swap" popover for exercises whose template
// carried alternatives. Picking an alt calls onSwap({name, sets, ...})
// on the parent editor which replaces the exercise's dose fields in
// place. The alternatives list itself stays intact so the reviewer
// can still swap back.
// ---------------------------------------------------------------------------
function AlternativesSwap({ alternatives, currentName, onSwap }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        size="sm" variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="h-7 text-xs"
        title="Swap for a clinic-approved alternative"
        data-testid="ep-exercise-swap"
      >
        <Copy className="h-3 w-3 mr-1" /> Swap
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-20 min-w-[240px] max-w-xs
                          bg-white border border-zinc-200 rounded-md shadow-lg p-1">
            <div className="text-2xs uppercase text-zinc-500 px-2 pt-1 pb-0.5">
              Alternatives
            </div>
            {alternatives.map((alt, ai) => (
              <button
                key={ai}
                onClick={() => { onSwap(alt); setOpen(false); }}
                className={cn(
                  "w-full text-left px-2 py-1.5 rounded text-xs hover:bg-indigo-50",
                  alt.name === currentName && "bg-indigo-50/70",
                )}
                data-testid={`ep-alt-${ai}`}
              >
                <div className="font-medium">{alt.name}</div>
                <div className="text-2xs text-zinc-500">
                  {[alt.sets && `${alt.sets}×${alt.reps || ""}`,
                    alt.load, alt.tempo, alt.rest]
                    .filter(Boolean).join(" · ") || "—"}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// SaveAsTemplateDialog — takes the current program and creates a fresh
// clinic template. Name defaults to the program title so it's a
// one-click "save my tweaks" for owners and managers.
// ---------------------------------------------------------------------------
function SaveAsTemplateDialog({ open, onOpenChange, program }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && program) {
      setName(`${program.title || "Program"} (template)`);
      setDescription("");
      setTags((program.tags || []).join(", "));
    }
  }, [open, program]);

  async function save() {
    if (!program?.id || !name.trim()) return;
    setSaving(true);
    try {
      await api.post(`/exercise-programs/${program.id}/save-as-template`, {
        name: name.trim(),
        short_description: description.trim() || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Saved as template", {
        description: "Your clinic library now includes this program.",
      });
      onOpenChange(false);
    } catch (e) {
      toast.error("Save-as-template failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="save-as-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-indigo-700" /> Save as template
          </DialogTitle>
          <DialogDescription>
            Freeze this program&apos;s sections and exercises into a reusable
            clinic template. The client&apos;s current program is unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-2xs uppercase text-zinc-500">Template name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="save-as-name" />
          </div>
          <div>
            <label className="text-2xs uppercase text-zinc-500">Short description</label>
            <Textarea rows={2} value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional — what this template is for" />
          </div>
          <div>
            <label className="text-2xs uppercase text-zinc-500">Tags (comma-separated)</label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !name.trim()}
                  data-testid="save-as-confirm">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/**
 * SortableSection — wraps a program section with @dnd-kit sortable
 * plumbing without absorbing the section's ~60 lines of markup. The
 * children render-prop receives the drag handle bindings so the
 * grip icon can be placed exactly where it belongs (inside the
 * section header), while the outer wrapper carries the transform
 * transition + z-index bump during drag.
 */
function SortableSection({ id, children, testId }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, data: { type: "section" } });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "border rounded-md p-3",
        isDragging
          ? "border-indigo-300 shadow-md bg-indigo-50/40"
          : "border-zinc-200",
      )}
      data-testid={testId}
    >
      {children({ attributes, listeners })}
    </div>
  );
}


/**
 * SortableExerciseRow — wraps an exercise row so it participates in
 * the nested per-section SortableContext. Attaches type + parent
 * section metadata via `data` so the top-level DndContext handler
 * can distinguish exercise vs section drags and resolve source /
 * destination sections by their stable `_dnd_id`.
 */
function SortableExerciseRow({ exId, sectionDndId, children }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: `ex:${exId}`,
    data: { type: "exercise", exId, sectionDndId },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
}


/**
 * SectionDropZone — a droppable strip at the bottom of a section
 * body. Lets operators drop an exercise onto an empty section (or
 * append to a populated section without having to hover exactly
 * over its last row).
 */
function SectionDropZone({ sectionDndId, empty, testId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `secdrop:${sectionDndId}`,
    data: { type: "section-dropzone", sectionDndId },
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={testId}
      className={cn(
        "rounded border border-dashed text-2xs text-center transition-colors",
        empty ? "py-4 px-2" : "py-1.5 px-2",
        isOver
          ? "border-indigo-400 bg-indigo-50 text-indigo-700"
          : "border-zinc-200 text-zinc-400",
      )}
    >
      {isOver
        ? "Drop here"
        : empty
          ? "Drop an exercise here"
          : "Drop to move to end of section"}
    </div>
  );
}



// ---------------------------------------------------------------------------
// AutoSaveIndicator — a Google-Docs-style status pill for the header.
// Renders one of four states:
//   idle   → nothing (component returns null)
//   saving → grey "Autosaving…"
//   saved  → grey "Saved <relative>" (updates on 15s tick)
//   error  → red   "Autosave failed" with a Retry button
// The parent is expected to handle "retry" by wiring the manual
// saveDraft button (also visible in the sidebar Actions panel).
// ---------------------------------------------------------------------------
function AutoSaveIndicator({ state, lastSavedAt }) {
  // Tick a re-render every 15s so "Saved 30s ago" stays fresh
  // without needing to spam updates from the parent.
  const [, tick] = useState(0);
  useEffect(() => {
    if (state !== "saved" || !lastSavedAt) return;
    const id = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, [state, lastSavedAt]);

  if (state === "idle") return null;

  if (state === "saving") {
    return (
      <span
        className="inline-flex items-center gap-1 text-2xs text-zinc-500"
        data-testid="ep-autosave-indicator"
        data-state="saving"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Autosaving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span
        className="inline-flex items-center gap-1 text-2xs text-zinc-500"
        data-testid="ep-autosave-indicator"
        data-state="saved"
        title={
          lastSavedAt
            ? `Last saved ${new Date(lastSavedAt).toLocaleTimeString()}`
            : undefined
        }
      >
        <CheckCircle2 className="h-3 w-3 text-emerald-600" />
        {relativeTime(lastSavedAt)}
      </span>
    );
  }
  // error
  return (
    <span
      className="inline-flex items-center gap-1 text-2xs text-red-700"
      data-testid="ep-autosave-indicator"
      data-state="error"
      title="Your last edit couldn't autosave — try the Save draft button on the right."
    >
      <AlertTriangle className="h-3 w-3" />
      Autosave failed
    </span>
  );
}

function relativeTime(ts) {
  if (!ts) return "Saved";
  const diff = Math.max(0, Date.now() - ts);
  if (diff < 5_000) return "Saved just now";
  if (diff < 60_000) return `Saved ${Math.round(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `Saved ${Math.round(diff / 60_000)}m ago`;
  return `Saved ${Math.round(diff / 3_600_000)}h ago`;
}
